import { fetchClinicData } from '@/entities/clinic';
import {
  fetchInboxDetails,
  fetchReportStatus,
  handleDownloadPdfReport,
  handleMarkStatusReport,
  isNotAllowedViewReport,
} from '@/entities/inbox';
import { fetchUserSignature } from '@/entities/user';
import { useLoadingStore } from '@/shared/model';
import { EMITTER_CONSTANTS, LOADING_TYPES } from '@/shared/config';
import { STUDY_STATUS, isC1cStudy } from '@/entities/study';
import {
  generateToken,
  auth,
  emitter,
  generateFullName,
  dateToString,
  SocketIo,
  showMessageError,
  showMessageInfo,
} from '@/shared/lib';
import { useEmitter, useSetState, useUpdateEffect } from '@/shared/lib/customHooks';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { t } from 'ttag';

/**
 * Drives the inbox report detail page: loading one report, its PDF and physician signature, its status, the ECG viewer and the information drawers. Fetches and flattens the report for the reportId route param, downloads the PDF with throttled progress, joins the device's socket room, and listens on the inbox, mark-inbox, report-updated, mark-status, study-updated and study-deleted emitter events to patch, reload or abandon the report as it changes.
 *
 * @returns Report state together with the navigation, drawer, status, ECG-viewer and post-save handlers the detail page binds.
 */
const useInboxDetailsPage = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isStudyManagementPage = location.pathname.includes('studies/');
  /**
   * Provides the global loading-text setter used to show the view-report and view-ECG overlays. Selects setLoadingText from the loading store.
   *
   * @returns The store action that sets or clears the page loading text.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);
  const abortControllerRef = useRef<AbortController | null>(null);
  const joinedDeviceIdsRef = useRef<string[]>([]);

  const [state, setState] = useSetState({
    reportData: undefined as any,
    isLoadingReportDetail: false,
    downloadProgress: 0,
    downloadEstimated: null as number | null,
    inboxECGViewerData: undefined as any,
    isVisibleStudyInfoDrawer: false,
    isVisiblePatientInfoDrawer: false,
    clickedData: null as any,
    listChangingStatusReport: [] as any[],
    hasClosedInstantReportAlert: false,
    hasLoginBanner: auth.isShowLoginByBanner(),
  });

  const { reportData, isLoadingReportDetail } = state;
  const isInstantReport = reportData?.type === 'UNREVIEWED';
  /**
   * Flags whether the open instant report's study already holds a sent end-of-use report. True only when the loaded report is UNREVIEWED and some sibling report of type 'Study' carries an inbox date.
   *
   * @returns True when the instant report's study has a sent end-of-use report.
   */
  const hasVerifiedReport =
    isInstantReport &&
    _.some(reportData?.study?.reports, (x: any) => x.type === 'Study' && !!x.inbox?.date);

  /**
   * Publishes the report PDF download progress into page state. Throttles the setState call to one leading update every 100ms and is memoised for the lifetime of the hook.
   *
   * @param downloadProgress - Download completion ratio reported by the request.
   * @returns The throttled setter for the download progress.
   */
  const throttleSetProgressDownload = useCallback(
    _.throttle((downloadProgress: number) => setState({ downloadProgress }), 100, {
      leading: true,
    }),
    [],
  );

  /**
   * Publishes the estimated remaining time of the report PDF download into page state. Throttles the setState call to one leading update every 500ms and is memoised for the lifetime of the hook.
   *
   * @param downloadEstimated - Estimated seconds remaining reported by the download.
   * @returns The throttled setter for the download estimate.
   */
  const throttleSetEstimateDownload = useCallback(
    _.throttle((downloadEstimated: number) => setState({ downloadEstimated }), 500, {
      leading: true,
    }),
    [],
  );

  /**
   * Subscribes to realtime report updates for the device that recorded the report. Reads study.deviceId and, when it is present, not the literal 'undefined' and not already tracked, appends it to the joined-device ref and emits the socket room report-updated-`<deviceId>`.
   *
   * @param report - Report supplying the study deviceId to subscribe to.
   * @returns Nothing.
   */
  const joinDeviceRoom = (report: any) => {
    const deviceId = report?.study?.deviceId;
    if (deviceId && deviceId !== 'undefined' && !joinedDeviceIdsRef.current.includes(deviceId)) {
      joinedDeviceIdsRef.current = [...joinedDeviceIdsRef.current, deviceId];
      (SocketIo as any).emitRoom(`report-updated-${deviceId}`);
    }
  };

  /**
   * Leaves the inbox report detail view and returns the user to the list it was opened from. Aborts any in-flight PDF or signature download first, then routes to /studies/`<studyId>` carrying a flag that selects the instant-report tab for UNREVIEWED reports or the report tab otherwise when opened from study management, and replaces the route with /inbox in every other case.
   *
   * @param reportData - Report whose study id and type decide the destination; defaults to the loaded report.
   * @returns Nothing.
   */
  const handleGoBack = (reportData = state?.reportData) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    const studyId = reportData?.study?.id || '';
    if (isStudyManagementPage && studyId) {
      const isInstantRp = reportData?.type === 'UNREVIEWED';
      const navigateState = isInstantRp
        ? {
            shouldViewInstantReportTab: true,
          }
        : { shouldShoulViewReportTab: true };
      navigate(`/studies/${studyId}`, { state: navigateState });
    } else {
      navigate('/inbox', { replace: true });
    }
  };

  /**
   * Downloads the report PDF, and the signing clinic physician's signature, into the loaded report. Returns early when a pdfArrayBuffer is already present, aborts any previous download, requests the PDF url and the signature in parallel, streams the PDF as an arraybuffer while throttling progress and estimate updates, and base64-encodes the signature; aborted requests are ignored and other failures clear reportData unless it is an artifact report.
   *
   * @param reportData - Report view model to enrich with the downloaded PDF and signature.
   * @returns A promise that settles once the report is loaded or the failure handled.
   */
  const handleLoadReportDetail = async (reportData: any) => {
    if (!reportData) return;

    if (reportData?.pdfArrayBuffer) {
      setState({ reportData, isLoadingReportDetail: false });
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const promises: Promise<any>[] = [
      handleDownloadPdfReport({
        id: reportData.id,
        studyFid: reportData.studyFid,
        reportType: reportData.type,
        reportId: reportData.reportId,
        eventFid: reportData.eventFid,
      }),
    ];

    if (auth.isClinicPhysician()) {
      const userResult = await fetchUserSignature(auth.userId());
      const { data: userData } = userResult;
      const { user } = userData;
      if (user?.signature) {
        const accessToken = await generateToken();
        promises.push(
          axios.get(user.signature, {
            responseType: 'arraybuffer',
            headers: { 'access-token': accessToken },
            signal: abortController.signal,
          }),
        );
      }
    }

    try {
      const results = await Promise.all(promises);

      if (abortController.signal.aborted) return;
      const { data: downloadPdfReportData } = results[0];
      const { downloadPdfReport } = downloadPdfReportData;
      if (downloadPdfReport.isSuccess) {
        const accessToken = await generateToken();
        const response = await axios.get(downloadPdfReport.url, {
          responseType: 'arraybuffer',
          headers: { 'access-token': accessToken },
          signal: abortController.signal,
          onDownloadProgress: ({ progress, estimated }) => {
            if (abortController.signal.aborted) return;
            if (progress != null) throttleSetProgressDownload(progress);
            if (estimated != null) throttleSetEstimateDownload(estimated);
          },
        });

        if (abortController.signal.aborted) return;

        _.assign(reportData, {
          url: downloadPdfReport.url,
          pdfArrayBuffer: response.data,
          totalStrips: downloadPdfReport?.totalStrips,
        });
      } else {
        console.error('Failed to download PDF report: ', downloadPdfReport.message);
      }

      if (auth.isClinicPhysician()) {
        const getSignatureResponse = results[1];
        if (!_.isEmpty(getSignatureResponse) && getSignatureResponse.status === 200) {
          const buffer = Buffer.from(getSignatureResponse.data);
          const base64String = buffer.toString('base64');
          _.assign(reportData, { signatureBase64String: `data:image/png;base64,${base64String}` });
        }
      }

      if (abortController.signal.aborted) return;

      setState({ reportData, isLoadingReportDetail: false });
    } catch (error: any) {
      if (
        abortController.signal.aborted ||
        error.name === 'AbortError' ||
        error.name === 'CanceledError'
      ) {
        return;
      }
      if (reportData.isArtifactReport) {
        setState({ isLoadingReportDetail: false });
      } else {
        showMessageError({ content: error.message });
        setState({ reportData: undefined, isLoadingReportDetail: false });
      }
    }
  };

  /**
   * Loads one inbox report and reshapes it into the flat view model the detail page renders. Joins the device's realtime room, flattens study, patient, physician, facility, event and format fields and derives eventTime and the dateOfService range; a report with no inbox date warns and goes back, one blocked by facility arrears goes back and raises the escalation popup, a report of a CardioOne study goes back and raises the CardioOne report notice, otherwise state is armed for the PDF download.
   *
   * @param id - Inbox report id to load.
   * @returns A promise that settles once the report has been stored or the page has navigated away.
   */
  const fetchAndSetReport = async (id: string) => {
    try {
      loadingPageAction(LOADING_TYPES.VIEW_REPORT);
      const report = await fetchInboxDetails(id);

      joinDeviceRoom(report);

      const reportData = {
        ...report,
        id: report?.id,
        reportId: report?.reportId,
        type: report?.type,
        start: report?.start,
        stop: report?.stop,
        studyFid: report?.study?.friendlyId,
        interprettingPhysicianName: generateFullName(report?.study?.info?.interprettingPhysician),
        patientName: generateFullName(report?.study?.info?.patient),
        study: report?.study,
        studyType: report?.study?.studyType,
        timezone: report?.study?.timezone,
        eventFid: report?.event?.friendlyId,
        physicianInterpretation: report?.physicianInterpretation,
        technicianComments: report?.technicianComments,
        date: report?.inbox?.date,
        priority: report?.inbox?.priority,
        isReviewed: report?.inbox?.isReviewed,
        isRead: report?.inbox?.isRead,
        eventTimeUTC: report?.start,
        eventTime: dateToString({
          startStudyDate: report?.study?.start ?? undefined,
          isoDate: report?.start ?? undefined,
          timezone: report?.study?.timezone ?? undefined,
          studyTimeOffset: report?.study?.studyTimeOffset ?? undefined,
          facilityCountry: report?.study?.facility?.contact?.country ?? undefined,
          facilityTimezone: report?.study?.facility?.timezone ?? undefined,
        }),
        dateOfService: `${dateToString({
          isoDate: report?.start ?? undefined,
          timezone: report?.study?.timezone ?? undefined,
          studyTimeOffset: report?.study?.studyTimeOffset ?? undefined,
          format: 'MM/DD/YY',
        })} - ${dateToString({
          isoDate: report?.stop ?? undefined,
          timezone: report?.study?.timezone ?? undefined,
          studyTimeOffset: report?.study?.studyTimeOffset ?? undefined,
          format: 'MM/DD/YY',
        })}`,
        eventId: report?.event?.id,
        hasClinicData: report?.event?.hasClinicData,
        isNotificationReport: report?.type === 'Notification',
        isArtifactReport: report?.isArtifactReport,
        facility: report?.study?.facility,
        isNewFormat: report?.isNewFormat,
        formatVersion: report?.formatVersion,
        aiFormatVersion: report?.aiFormatVersion,
        statusReport: report?.statusReport,
        lastStatusReportAt: report?.lastStatusReportAt,
        isSavingStatus: false,
        profile: report?.profile,
        status: report?.status,
        isNonPhi: report?.study?.isNonPhi,
      };

      const isNotSentReport = _.isNil(reportData.date);
      if (isNotSentReport) {
        showMessageError({ content: t`Report has not been sent yet!` });
        handleGoBack(reportData);
      } else if (isNotAllowedViewReport(reportData)) {
        handleGoBack(reportData);
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_ESCALATION, reportData.facility?.arrears);
      } else if (isC1cStudy(reportData.study ?? {})) {
        handleGoBack(reportData);
        emitter.emit(EMITTER_CONSTANTS.SHOW_POPUP_C1C_REPORT);
      } else {
        setState({
          reportData,
          isLoadingReportDetail: true,
          downloadProgress: 0,
          downloadEstimated: null,
        });
      }
    } catch (error: any) {
      console.error('Failed to fetch report detail: ', error);
      showMessageError({ content: error.message });
      navigate(isStudyManagementPage ? '/studies' : '/inbox', { replace: true });
    } finally {
      loadingPageAction();
    }
  };

  useEffect(() => {
    if (reportId) {
      fetchAndSetReport(reportId);
    }
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [reportId]);

  useUpdateEffect(() => {
    if (state.isLoadingReportDetail) {
      handleLoadReportDetail(state.reportData);
    }
  }, [state.isLoadingReportDetail, state.reportData]);

  /**
   * Opens or closes the ECG viewer for an inbox report. With a clicked report it raises the view-ECG loading state, builds the viewer payload from study friendly id, patient and physician names, study start, event time and facility timezone and country, fetches the event's clinic data into it and stores it; without one it clears inboxECGViewerData, and a failed clinic-data fetch is logged and surfaced.
   *
   * @param clickedData - Report to view, or omitted to close the viewer.
   * @returns A promise that settles once the viewer payload is stored or cleared.
   */
  const toggleInboxECGViewer = async (clickedData?: any) => {
    if (clickedData) {
      loadingPageAction(LOADING_TYPES.VIEW_ECG);
      const inboxECGViewerData = {
        studyFid: clickedData.studyFid,
        patientName: clickedData.patientName,
        interprettingPhysicianName: clickedData.interprettingPhysicianName,
        startStudyDate: clickedData.study.start,
        eventTime: clickedData.eventTimeUTC,
        timezone: clickedData.timezone,
        studyTimeOffset: clickedData.study?.studyTimeOffset,
        facilityCountry: clickedData.study?.facility?.contact?.country,
        facilityTimezone: clickedData.study?.facility?.timezone,
      };
      try {
        const clinicData = await fetchClinicData({
          studyId: clickedData.study?.id,
          eventId: clickedData.eventId,
        });
        _.assign(inboxECGViewerData, { clinicData });
        setState({ inboxECGViewerData });
      } catch (error: any) {
        console.error('Failed to fetch clinic data: ', error);
        showMessageError({ content: error.message });
      } finally {
        loadingPageAction();
      }
    } else {
      setState({ inboxECGViewerData: undefined });
    }
  };

  /**
   * Opens the ECG viewer for the report currently shown on the detail page. Forwards state.reportData to toggleInboxECGViewer, which fetches the event's clinic data and builds the viewer payload.
   *
   * @returns Nothing.
   */
  const handleClickViewEcgViewer = () => {
    toggleInboxECGViewer(state.reportData);
  };

  /**
   * Opens or closes the study-information and patient-information drawers of the report detail page. Stores the clicked row and, selecting on the drawer title, flips isVisibleStudyInfoDrawer or isVisiblePatientInfoDrawer, closing both when the title matches neither.
   *
   * @param clickedData - Report or row whose details the drawer shows.
   * @param title - Drawer title selecting which drawer to toggle.
   * @returns Nothing.
   */
  const toggleDisplayDrawer = (clickedData: any = null, title = '') => {
    const setStateObject: any = { clickedData };
    switch (title) {
      case t`Study information`:
        _.assign(setStateObject, { isVisibleStudyInfoDrawer: !state.isVisibleStudyInfoDrawer });
        break;
      case t`Patient information`:
        _.assign(setStateObject, { isVisiblePatientInfoDrawer: !state.isVisiblePatientInfoDrawer });
        break;
      default:
        _.assign(setStateObject, {
          isVisibleStudyInfoDrawer: false,
          isVisiblePatientInfoDrawer: false,
        });
    }
    setState(setStateObject);
  };

  /**
   * Applies edits made in the details view, such as a new signature, findings or PDF bytes, to the
   * open inbox report. Merges the given fields into a deep clone of the previous reportData and
   * stores it back on state.
   *
   * @param data - Signature, physician interpretation or PDF buffer to merge into the report.
   * @returns Nothing.
   */
  const updateClickedReport = (data: {
    signatureBase64String?: string;
    physicianInterpretation?: string;
    pdfArrayBuffer?: any;
  }) => {
    setState((prevState: any) => {
      const clonedReportData = _.cloneDeep(prevState.reportData);
      _.assign(clonedReportData, data);
      return { reportData: clonedReportData };
    });
  };

  /**
   * Reflects a read or unread change made elsewhere on the open inbox report. Copies isRead onto a deep clone of reportData only when the id matches the currently loaded report, otherwise leaves state untouched.
   *
   * @param id - Report id the read change applies to.
   * @param isRead - New read flag for the report.
   * @returns Nothing.
   */
  const handleAfterReadingReport = ({ id, isRead }: { id: string; isRead: boolean }) => {
    setState((prevState: any) => {
      if (prevState.reportData?.id === id) {
        const cloned = _.cloneDeep(prevState.reportData);
        cloned.isRead = isRead;
        return { reportData: cloned };
      }
      return {};
    });
  };

  /**
   * Refreshes the open report's status after its PDF has been saved. Fetches the report status for the given inbox id and, when it belongs to the loaded report and its lastStatusReportAt is newer, copies statusReport and lastStatusReportAt onto a clone of reportData; a failed fetch is only logged.
   *
   * @param inboxId - Inbox report id whose status is re-read.
   * @returns A promise that settles once the status has been applied or the failure logged.
   */
  const handleAfterSavingPdfReport = async (inboxId: string) => {
    try {
      const report = await fetchReportStatus(inboxId);
      setState((prevState: any) => {
        if (prevState.reportData?.id === report?.id) {
          const cloned = _.cloneDeep(prevState.reportData);
          const shouldUpdate = cloned.lastStatusReportAt
            ? dayjs(report?.lastStatusReportAt).isAfter(dayjs(cloned.lastStatusReportAt))
            : true;
          if (shouldUpdate) {
            cloned.statusReport = report?.statusReport;
            cloned.lastStatusReportAt = report?.lastStatusReportAt;
            return { reportData: cloned };
          }
        }
        return {};
      });
    } catch (error: any) {
      console.error('Failed to fetch report status: ', error);
    }
  };

  /**
   * Persists a report status change from the detail page and reconciles the optimistic state. Calls the mark-status mutation, then hands the confirmed status on success or the tracked prevStatus on rejection to applyResult, which prunes the pending entry and lowers isSavingStatus; a thrown error is logged, surfaced and rolled back the same way.
   *
   * @param id - Report id whose status is being saved.
   * @param statusReport - Status selected by the user.
   * @returns A promise that settles once the status has been confirmed or rolled back.
   */
  const handleUpdateStatusReportDetail = async (id: string, statusReport: string) => {
    /**
     * Clears the pending status-change bookkeeping for a report once its status mutation settles. Drops the report's entry from listChangingStatusReport, writes resolvedStatus onto a deep clone of reportData falling back to the tracked prevStatus when it is undefined, and lowers isSavingStatus.
     *
     * @param prevState - State snapshot the update is derived from.
     * @param resolvedStatus - Status confirmed by the server, or undefined to roll back to the tracked previous status.
     * @returns Partial state holding the pruned pending list and the updated report.
     */
    const applyResult = (prevState: any, resolvedStatus: string | undefined) => {
      /**
       * Locates the pending status-change entry recorded for the report being updated. Searches listChangingStatusReport in the previous state for the item whose id matches the report id so its prevStatus can be restored.
       *
       * @returns The matching pending status-change entry, or undefined when none is tracked.
       */
      const prevEntry = prevState.listChangingStatusReport.find((item: any) => item.id === id);
      const clonedReportData = prevState.reportData
        ? _.cloneDeep(prevState.reportData)
        : prevState.reportData;
      if (clonedReportData) {
        clonedReportData.statusReport = resolvedStatus ?? prevEntry?.prevStatus;
        clonedReportData.isSavingStatus = false;
      }
      return {
        listChangingStatusReport: prevState.listChangingStatusReport.filter(
          (item: any) => item.id !== id,
        ),
        reportData: clonedReportData,
      };
    };

    try {
      const result = await handleMarkStatusReport({ id, statusReport });
      setState((prevState: any) => {
        /**
         * Locates the pending status-change entry recorded for the report being updated. Searches listChangingStatusReport in the previous state for the item whose id matches the report id, supplying the fallback status when the mutation did not succeed.
         *
         * @returns The matching pending status-change entry, or undefined when none is tracked.
         */
        const prevEntry = prevState.listChangingStatusReport.find((item: any) => item.id === id);
        return applyResult(prevState, result.isSuccess ? statusReport : prevEntry?.prevStatus);
      });
    } catch (error: any) {
      console.error('Failed to update report status: ', error);
      showMessageError({ content: error.message });
      setState((prevState: any) => applyResult(prevState, undefined));
    }
  };

  /**
   * Applies a status pick on the report detail page optimistically before saving it. Writes the new statusReport with isSavingStatus onto a clone of reportData, records an entry in listChangingStatusReport holding the previous status for rollback, then delegates the mutation to handleUpdateStatusReportDetail.
   *
   * @param value - Status selected by the user.
   * @param id - Report id being updated.
   * @returns Nothing.
   */
  const onChangeStatusReportDetail = (value: string, id: string) => {
    setState((prevState: any) => {
      const clonedReportData = prevState.reportData
        ? _.cloneDeep(prevState.reportData)
        : prevState.reportData;
      if (clonedReportData) {
        clonedReportData.statusReport = value;
        clonedReportData.isSavingStatus = true;
      }
      return {
        listChangingStatusReport: [
          ...prevState.listChangingStatusReport,
          {
            id,
            status: value,
            prevStatus: clonedReportData?.statusReport ?? prevState.reportData?.statusReport,
            isChangingStatus: true,
          },
        ],
        reportData: clonedReportData,
      };
    });
    handleUpdateStatusReportDetail(id, value);
  };

  /**
   * Records that the physician dismissed the instant-report alert on the detail page. Sets hasClosedInstantReportAlert so the alert is not shown again.
   *
   * @returns Nothing.
   */
  const afterCloseInstantReportAlert = () => {
    setState({ hasClosedInstantReportAlert: true });
  };

  /**
   * Sends the physician from an instant report to the verified end-of-use report. Replaces the current route with /studies/`<study id>` carrying shouldLoadVerifiedReport so the study page opens that report.
   *
   * @returns Nothing.
   */
  const handleViewVerifiedReportClick = () => {
    navigate(`/studies/${state.reportData?.study?.id}`, {
      replace: true,
      state: { shouldLoadVerifiedReport: true },
    });
  };

  /**
   * Tracks whether the login-by banner is shown above the inbox report details page. Stores the
   * banner visibility as the hasLoginBanner flag on state.
   *
   * @param isShowLoginByBanner - Whether the login-by banner is visible.
   * @returns Nothing.
   */
  const handleLoginBannerDisplayChange = ({
    isShowLoginByBanner,
  }: {
    isShowLoginByBanner: boolean;
  }) => {
    setState({ hasLoginBanner: isShowLoginByBanner });
  };

  useEffect(() => {
    /**
     * Reacts to the open report's study being aborted or deleted while it is on screen. Shows an informational message that the report is no longer available and leaves the page through handleGoBack, which aborts pending downloads and routes back to the study or inbox list.
     *
     * @returns Nothing.
     */
    const handleNoLongerAvailableReport = () => {
      showMessageInfo({ content: t`This report is no longer available` });
      handleGoBack(state.reportData);
    };

    /**
     * Keeps the opened report in sync with inbox socket events, including recall. Registers an emitter listener that ignores other report ids, shows a 'report is recalled' message and navigates back when the message is a recall, and otherwise merges the message's inbox fields into a deep clone of reportData.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const inboxEventListener = emitter.addListener(EMITTER_CONSTANTS.INBOX_EVENTS, (msg: any) => {
      if (msg.id !== state.reportData?.id) return;
      if (msg.recalled) {
        showMessageInfo({ content: t`This report is recalled` });
        handleGoBack(state.reportData);
      } else {
        setState((prevState: any) => {
          const cloned = _.cloneDeep(prevState.reportData);
          _.assign(cloned, msg.inbox);
          return { reportData: cloned };
        });
      }
    });

    /**
     * Keeps the opened report in sync with mark-inbox socket events. Registers an emitter listener that ignores messages for other report ids and otherwise merges the whole message into a deep clone of reportData.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const markInboxListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_INBOX_EVENT,
      (msg: any) => {
        if (msg.id !== state.reportData?.id) return;
        setState((prevState: any) => {
          const cloned = _.cloneDeep(prevState.reportData);
          _.assign(cloned, msg);
          return { reportData: cloned };
        });
      },
    );

    /**
     * Keeps the opened report in sync with report-updated socket events, including PDF regeneration. Ignores messages for other reports or with a mismatched/absent isNonPhi flag, applies the new status and isRenderingBackground, and when the report just became Ready or finished background rendering clears pdfArrayBuffer/url and restarts the download by resetting progress and raising isLoadingReportDetail.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const reportUpdatedListener = emitter.addListener(
      EMITTER_CONSTANTS.REPORT_UPDATED,
      (msg: any) => {
        if (
          msg.id !== state.reportData?.id ||
          _.isNil(state.reportData?.isNonPhi) ||
          _.isNil(msg.isNonPhi) ||
          state.reportData.isNonPhi !== msg.isNonPhi
        )
          return;

        setState((prevState: any) => {
          const cloned = _.cloneDeep(prevState.reportData);
          const previousStatus = cloned.status;
          cloned.status = msg.status;

          let shouldReloadReportBackground = false;
          if (
            'isRenderingBackground' in msg &&
            msg.isRenderingBackground !== cloned.isRenderingBackground
          ) {
            cloned.isRenderingBackground = msg.isRenderingBackground;
            shouldReloadReportBackground = msg.isRenderingBackground === false;
          }

          const shouldLoadNewReport =
            (msg.status === 'Ready' && previousStatus !== 'Ready') || shouldReloadReportBackground;
          if (shouldLoadNewReport) {
            _.assign(cloned, { pdfArrayBuffer: undefined, url: undefined });
            return {
              reportData: cloned,
              isLoadingReportDetail: true,
              downloadProgress: 0,
              downloadEstimated: null,
            };
          }
          return { reportData: cloned };
        });
      },
    );

    /**
     * Keeps the opened report's review status in sync with mark-status-report socket events. Registers an emitter listener that ignores other report ids and only copies statusReport and lastStatusReportAt when the message timestamp is newer than the stored one, or when no timestamp is stored yet.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const markStatusReportListener = emitter.addListener(
      EMITTER_CONSTANTS.MARK_STATUS_REPORT,
      (msg: any) => {
        if (msg.id !== state.reportData?.id) return;
        setState((prevState: any) => {
          const cloned = _.cloneDeep(prevState.reportData);
          const shouldUpdate = cloned.lastStatusReportAt
            ? dayjs(msg.lastStatusReportAt).isAfter(dayjs(cloned.lastStatusReportAt))
            : true;
          if (shouldUpdate) {
            cloned.statusReport = msg.statusReport;
            cloned.lastStatusReportAt = msg.lastStatusReportAt;
            return { reportData: cloned };
          }
          return {};
        });
      },
    );

    /**
     * Closes the report detail view when the report's study is aborted. Registers an emitter listener that reacts only to study-updated messages carrying the ABORTED status for the opened report's study and then runs the no-longer-available handling.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const studyUpdatedListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_UPDATED_EVENT,
      (msg: any) => {
        if (msg.status === STUDY_STATUS.ABORTED && state.reportData?.study?.id === msg.id) {
          handleNoLongerAvailableReport();
        }
      },
    );

    /**
     * Closes the report detail view when the report's study is deleted. Registers an emitter listener that scans the study-deleted payload for an entry whose studyId matches the opened report's study and then runs the no-longer-available handling.
     *
     * @returns The emitter subscription, removed when the effect is cleaned up.
     */
    const studyDeletedListener = emitter.addListener(
      EMITTER_CONSTANTS.STUDY_DELETED,
      (msg: any) => {
        if (_.find(msg, (x: any) => x.studyId === state.reportData?.study?.id)) {
          handleNoLongerAvailableReport();
        }
      },
    );

    return () => {
      inboxEventListener.remove();
      markInboxListener.remove();
      reportUpdatedListener.remove();
      markStatusReportListener.remove();
      studyUpdatedListener.remove();
      studyDeletedListener.remove();
    };
  }, [state.reportData]);

  useEmitter(EMITTER_CONSTANTS.IS_SHOW_LOGIN_BY_BANNER, handleLoginBannerDisplayChange, []);

  return {
    state,
    setState,
    reportId,
    navigate,
    location,
    isStudyManagementPage,
    reportData,
    isLoadingReportDetail,
    isInstantReport,
    hasVerifiedReport,
    handleGoBack,
    handleClickViewEcgViewer,
    toggleInboxECGViewer,
    toggleDisplayDrawer,
    updateClickedReport,
    onChangeStatusReportDetail,
    afterCloseInstantReportAlert,
    handleViewVerifiedReportClick,
    handleAfterReadingReport,
    handleAfterSavingPdfReport,
  };
};

export { useInboxDetailsPage };
